//-- Copyright (c) 2021 TMT Insights Group, LLC
import styled from '@emotion/styled';
import { Button, Chip, Grid } from '@mui/material';
import { useContext } from 'react';

import AppliedFilters from '@/components/_common/Filters/AppliedFilters';
import Filter, { FilterProps, FilterSelectorOptions } from '@/components/_common/Filters/Filter';
import FilterBoolean from '@/components/_common/Filters/FilterBoolean';
import { FilterLabel } from '@/components/_common/Filters/FilterChip';
import FilterDateRange from '@/components/_common/Filters/FilterDateRange';
import FilterID from '@/components/_common/Filters/FilterID';
import FilterNullableBoolean from '@/components/_common/Filters/FilterNullableBoolean';
import FilterNumericRange from '@/components/_common/Filters/FilterNumericRange';
import FiltersDrawer from '@/components/_common/Filters/FiltersDrawer';
import FilterText from '@/components/_common/Filters/FilterText';
import SearchBar from '@/components/_common/SearchBar/SearchBar';
import useFiltersDrawer from '@/components/_hooks/useFiltersDrawer';
import { ConfigContext } from '@/contextAPI/configContext';
import { NewFilterTypesMap, PolarisIcon } from '@/exports/columnCommonComponents';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectCurrentTableKey, selectDataTableFilters, selectIsFiltered } from '@/redux/selectors/dataTableSelectors';
import { clearFilters } from '@/redux/slices/dataTableSlice';

export interface FiltersProps {
  filters: FilterProps[];
  visibleFilters: FilterProps[];
  visibleQuickFilters?: FilterProps[];
  anyAllFilters?: FilterProps[];
  openFilterLayoutsModal: () => void;
}

const Filters = ({ filters, visibleFilters = [], visibleQuickFilters = [], openFilterLayoutsModal, anyAllFilters }: FiltersProps) => {
  const filtersVersion = (useContext(ConfigContext)?.filtersVersion || 1) as number;
  const dispatch = useAppDispatch();
  const isFiltered = useAppSelector(selectIsFiltered);
  const currentTable = useAppSelector(selectCurrentTableKey);
  const appliedFilters = useAppSelector(selectDataTableFilters);
  const {
    isFiltersDrawerOpen,
    toggleFiltersDrawer,
    expandedFilters,
    toggleExpandedFilter,
    collapseAllFilters,
    advanceFilters,
    updateAdvanceFilters
  } = useFiltersDrawer({
    appliedFilters
  });

  const handleFiltersDrawerCancel = () => {
    toggleFiltersDrawer(false);
  };

  const renderFilters = (isQuickFilters = false) => {
    const filtersRef = filtersVersion === 2 && isQuickFilters && visibleQuickFilters ? visibleQuickFilters : visibleFilters;

    return filtersRef.map((filter) => {
      if (currentTable === 'focusSchedule' && filter.id === 'title_alt_client_id_filter' && !isQuickFilters) return;

      switch (filter.type) {
        case NewFilterTypesMap.ARRAY: {
          return (
            <FilterSelectorOptions
              key={filter.id}
              type={filter.id}
              searchable={filter.searchable || (filter.options && Object.keys(filter.options)?.length > 5)}
              label={filter.label}
              options={filter.options}
              singleSelection={filter.singleSelection}
              role={filter?.role}
              isQuickFilters={isQuickFilters}
              expandedFilters={expandedFilters}
              toggleExpandedFilter={toggleExpandedFilter}
              anyAllFilters={anyAllFilters}
              advanceFilters={advanceFilters}
              onAdvanceFiltersChange={updateAdvanceFilters}
            />
          );
        }
        case NewFilterTypesMap.SEARCH: {
          if (filtersVersion === 2) return;
          return <SearchBar key={filter.id} role={filter?.role} />;
        }
        case NewFilterTypesMap.DATE: {
          if (filter.field === 'term_start') return;
          return (
            <FilterDateRange
              key={filter.id}
              type={filter.id}
              field={filter.field}
              label={filter.label}
              role={filter?.role}
              isQuickFilters={isQuickFilters}
              expandedFilters={expandedFilters}
              onExpand={toggleExpandedFilter}
              advanceFilters={advanceFilters}
              onAdvanceFiltersChange={updateAdvanceFilters}
            />
          );
        }
        case NewFilterTypesMap.BOOLEAN: {
          return (
            <FilterBoolean
              key={filter.id}
              type={filter.id}
              label={filter.label}
              role={filter?.role}
              isQuickFilters={isQuickFilters}
              advanceFilters={advanceFilters}
              onAdvanceFiltersChange={updateAdvanceFilters}
            />
          );
        }
        case NewFilterTypesMap.USERS: {
          return (
            <Filter
              key={filter.id}
              type={filter.id}
              component={filter.component}
              searchable={filter.searchable}
              label={filter.label}
              options={filter.options}
              singleSelection={filter.singleSelection}
              renderApplied={filter.renderApplied}
              role={filter?.role}
              isQuickFilters={isQuickFilters}
              expandedFilters={expandedFilters}
              toggleExpandedFilter={toggleExpandedFilter}
              advanceFilters={advanceFilters}
              onAdvanceFiltersChange={updateAdvanceFilters}
            />
          );
        }
        case NewFilterTypesMap.ID: {
          return (
            <FilterID
              key={filter.id}
              type={filter.id}
              label={filter.label}
              excludeOptions={filter.excludeOptions}
              options={filter.options}
              role={filter?.role}
              isQuickFilters={isQuickFilters}
              behavior={filter.filterBehavior}
              expandedFilters={expandedFilters}
              onExpand={toggleExpandedFilter}
              advanceFilters={advanceFilters}
              onAdvanceFiltersChange={updateAdvanceFilters}
            />
          );
        }
        case NewFilterTypesMap.TEXT: {
          return (
            <FilterText
              key={filter.id}
              type={filter.id}
              component={filter.component}
              label={filter.label}
              options={filter.options}
              role={filter?.role}
            />
          );
        }
        case NewFilterTypesMap.NULLABLE_BOOLEAN: {
          return <FilterNullableBoolean key={filter.label.toLowerCase().replace(/ /g, '_')} data={filter as any} />;
        }
        case NewFilterTypesMap.NUMERIC_RANGE: {
          return <FilterNumericRange key={filter.id} type={filter.id} label={filter.label} limit={filter.limit} />;
        }
        case NewFilterTypesMap.PROFILES: {
          return (
            <Filter
              key={filter.id}
              type={filter.id}
              component={filter.component}
              searchable={filter.searchable}
              label={filter.label}
              options={filter.options}
              renderApplied={filter.renderApplied}
              isQuickFilters={isQuickFilters}
              expandedFilters={expandedFilters}
              toggleExpandedFilter={toggleExpandedFilter}
              advanceFilters={advanceFilters}
              onAdvanceFiltersChange={updateAdvanceFilters}
            />
          );
        }
      }
    });
  };

  if (filtersVersion === 2) {
    const searchFilter = visibleFilters.find((filter) => filter.type === NewFilterTypesMap.SEARCH);
    const handleClearAllFilters = () => {
      dispatch(clearFilters(undefined));
    };

    return (
      <Grid container rowGap={1} alignItems="center" className="filters-containter">
        {(visibleFilters.length > 1 || (visibleFilters.length === 1 && !searchFilter)) && (
          <StyledChip
            label={<FilterLabel>All Filters</FilterLabel>}
            variant="outlined"
            color="primary"
            icon={<PolarisIcon icon="filter" />}
            isFiltered={isFiltered}
            onClick={() => toggleFiltersDrawer(true)}
          />
        )}
        {visibleQuickFilters && renderFilters(true)}
        {isFiltered ? (
          <>
            <AppliedFilters filters={filters} visibleQuickFilters={visibleQuickFilters} />
            {searchFilter && <SearchBar role={searchFilter.role} />}
            <ClearAllFiltersButton
              color="primary"
              variant="text"
              startIcon={<PolarisIcon icon="close" height={14} />}
              onClick={handleClearAllFilters}
            >
              Clear all filters
            </ClearAllFiltersButton>
          </>
        ) : (
          searchFilter && <SearchBar role={searchFilter.role} />
        )}
        <FiltersDrawer
          open={isFiltersDrawerOpen}
          onCancel={handleFiltersDrawerCancel}
          expandedFilters={expandedFilters}
          openFilterLayoutsModal={openFilterLayoutsModal}
          onCollapseAll={collapseAllFilters}
          advanceFilters={advanceFilters}
        >
          {renderFilters(false)}
        </FiltersDrawer>
      </Grid>
    );
  }

  return renderFilters();
};

export default Filters;

const StyledChip = styled(Chip, { shouldForwardProp: (prop) => prop !== 'isFiltered' })<{ isFiltered: boolean }>(({ theme, isFiltered }) => ({
  marginRight: theme.spacing(1),
  color: isFiltered ? theme.palette.primary.main : 'inherit',
  height: 35,
  fontSize: theme.typography.body1.fontSize,
  borderRadius: 25
}));

const ClearAllFiltersButton = styled(Button)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
  marginLeft: theme.spacing(1)
}));
